import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import { ProviderContext } from "./Provider";
import styles from "./style.module.scss";
import HTMLViewport from "html-viewport";
import { Style, useLayoutEffect } from "./helpers";
import Modal from "./Components/Modal";
import BottomSheet from "./Components/BottomSheet";
import Popup from "./Components/Popup";
import { ContainerContext } from "./Context";
const Container = forwardRef((props, ref) => {
    const { bottomSheet, name, disableClose = false, backgroundClose = true, bottomSheetMaxWidth = 550, popup, theme = 'light', effect = 'scale', onHide } = props;
    const modalId = useId();
    const modalName = useMemo(() => name ?? modalId, [name, modalId]);
    const containerRef = useRef(null);
    const footerRef = useRef(null);
    const showStatus = useRef(false);
    const initPosition = useRef();
    const [isShow, setShow] = useState(false);
    const [isMobile, setMobile] = useState(false);
    const [footer, setFooter] = useState();
    const providerContext = useContext(ProviderContext);
    const listeners = useRef({
        onHide: () => { }
    });
    const show = () => {
        setShow(true);
    };
    const showPopup = (event) => {
        if (event && 'target' in event) {
            initPosition.current = event.target.getBoundingClientRect();
        }
        show();
    };
    const _hide = () => {
        const element = containerRef.current;
        if (!element)
            return;
        listeners.current.onHide();
        element.ontransitionend = () => {
            setShow(false);
        };
        element?.classList.remove(styles['active'], 'active');
    };
    const hide = () => {
        _hide();
    };
    const modalHide = () => {
        if (disableClose)
            return;
        _hide();
    };
    const backgroundHide = () => {
        if (!backgroundClose)
            return;
        modalHide();
    };
    const blockScroll = (status) => {
        if (typeof window === 'undefined')
            return;
        if (!document.body)
            return;
        const classBody = document.body.classList;
        status ? classBody.add(styles['block-scroll']) : classBody.remove(styles['block-scroll']);
    };
    const handleEscape = (e) => {
        if (e.code === 'Escape') {
            hide();
        }
    };
    const resetFocus = (e) => {
        if (!(e.relatedTarget && e.relatedTarget.closest('.' + styles['modal-container']))) {
            e.currentTarget.focus();
        }
    };
    const handleSize = (e) => {
        if (typeof window === 'undefined')
            return;
        const width = e ? e.target.innerWidth : window.innerWidth;
        const isCurrentMobile = width <= bottomSheetMaxWidth;
        if (isCurrentMobile !== isMobile)
            setMobile(isCurrentMobile);
    };
    useEffect(() => {
        showStatus.current = isShow;
        const viewport = new HTMLViewport;
        if (isShow) {
            viewport.add('interactive-widget', 'resizes-content');
            requestAnimationFrame(() => containerRef.current?.classList.add(styles['active'], 'active'));
        }
        else {
            setFooter(undefined);
            viewport.remove('interactive-widget');
            onHide?.();
        }
        requestAnimationFrame(() => {
            if (!isShow && providerContext.count() > 0)
                return;
            blockScroll(isShow);
        });
    }, [isShow]);
    useEffect(() => {
        return () => {
            setFooter(undefined);
            showStatus.current = false;
            blockScroll(false);
        };
    }, []);
    useLayoutEffect(() => {
        if (typeof window === 'undefined')
            return;
        handleSize();
        window.addEventListener('resize', handleSize);
        return () => {
            window.removeEventListener('resize', handleSize);
        };
    }, [isMobile, bottomSheetMaxWidth]);
    /**
     * Unset background cursor pointer
     */
    useLayoutEffect(() => {
        if (!containerRef.current)
            return;
        const container = containerRef.current;
        if (backgroundClose)
            container?.classList.remove(styles['cursor-unset']);
        else
            container?.classList.add(styles['cursor-unset']);
    }, [backgroundClose]);
    useLayoutEffect(() => {
        if (typeof window === 'undefined')
            return;
        providerContext.push(modalName, {
            show,
            hide: _hide,
            showStatus
        });
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);
    useImperativeHandle(ref, () => ({
        show,
        showPopup,
        hide,
        footerRef
    }));
    const classesRoot = useMemo(() => {
        const classesRoot = [
            Style('container')
        ];
        if (theme)
            classesRoot.push(theme);
        if (effect)
            classesRoot.push(styles['effect-' + effect]);
        if (bottomSheet)
            classesRoot.push(styles['bottomsheet']);
        return classesRoot.join(' ');
    }, [theme, effect, bottomSheet]);
    if (!providerContext.modals || !providerContext.modals.current) {
        return (null);
    }
    const renderProps = {
        ...props,
        showPopup,
        show,
        hide,
        footerRef
    };
    return createPortal(_jsx(ContainerContext.Provider, { value: {
            initPosition,
            footer,
            footerRef,
            listeners,
            setFooter,
            hide
        }, children: isShow &&
            _jsx("div", { className: classesRoot, ref: containerRef, children: (bottomSheet && isMobile) ?
                    _jsx(BottomSheet, { ...props, onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps }) : (popup ? _jsx(Popup, { ...props, onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps }) : _jsx(Modal, { ...props, onBackground: backgroundHide, onClose: modalHide, renderProps: renderProps })) }) }), providerContext.modals.current);
});
export { Container };
